import { Component } from '@theme/component';

/**
 * A small utility for setting up an animated disclosure panel using inert and CSS display grid for height auto animation.
 *
 * This component works with any element that follows the details/summary pattern for revealing content.
 * Forms and other interactive elements don't need to live inside a details element, but can still use
 * the expanded pattern for accessibility and animation.
 *
 * The component relies on CSS display grid and inert attributes to create smooth height animations
 * when expanding/collapsing content areas.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The disclosure trigger element.
 * @property {HTMLElement} disclosureContent – The disclosure content element.
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent'];

  connectedCallback() {
    super.connectedCallback();
    this.handleToggle = this.toggleDisclosure.bind(this);
    this.refs.disclosureTrigger.addEventListener('click', this.handleToggle, { passive: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.disclosureTrigger?.removeEventListener('click', this.handleToggle);
  }

  toggleDisclosure(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = this.refs.disclosureTrigger;
    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!expanded));

    const panel = this.refs.disclosureContent;
    if (panel) {
      panel.hidden = expanded;
    }
  }
}

if (!customElements.get('disclosure-custom')) {
  customElements.define('disclosure-custom', DisclosureCustom);
}